<template>
  <div class="site-prefs">
    <section
      v-if="canSwitchLocale"
      class="site-prefs__section"
    >
      <h3 class="site-prefs__heading">{{ t('common.preferences.language') }}</h3>
      <div class="site-prefs__row site-prefs__row--two">
        <button
          v-for="code in enabledLocales"
          :key="code"
          type="button"
          class="site-prefs__pill"
          :class="{ 'site-prefs__pill--active': currentLocale === code }"
          :lang="getLocaleMeta(code).language"
          @click="switchLocale(code)"
        >
          {{ getLocaleMeta(code).name }}
        </button>
      </div>
    </section>
    <div
      v-if="canSwitchLocale"
      class="site-prefs__divider"
    />
    <section class="site-prefs__section">
      <h3 class="site-prefs__heading">{{ t('common.preferences.currency') }}</h3>
      <div class="site-prefs__grid-currency">
        <button
          v-for="code in preferences.CURRENCY_OPTIONS"
          :key="code"
          type="button"
          class="site-prefs__pill site-prefs__pill--compact"
          :class="{ 'site-prefs__pill--active': preferences.currency === code }"
          @click="preferences.setCurrency(code)"
        >
          {{ code }}
        </button>
      </div>
    </section>
    <div class="site-prefs__divider" />
    <section class="site-prefs__section">
      <h3 class="site-prefs__heading">{{ t('common.preferences.measureUnits') }}</h3>
      <div class="site-prefs__row site-prefs__row--two">
        <button
          v-for="unit in preferences.MEASURE_OPTIONS"
          :key="unit"
          type="button"
          class="site-prefs__pill"
          :class="{ 'site-prefs__pill--active': preferences.measureUnit === unit }"
          @click="preferences.setMeasureUnit(unit)"
        >
          {{ unit === 'm2' ? 'm²' : 'ft²' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { getLocaleMeta } from '~/utils/i18n/locale';

defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const { enabledLocales, currentLocale, canSwitchLocale, switchLocale } = useLocaleSwitch();
const preferences = usePreferencesStore();
</script>

<style scoped lang="scss">
@use '@/styles/variables.scss' as *;

.site-prefs {
  box-sizing: border-box;
  width: 100%;
  min-width: min(100vw - 32px, 360px);
  max-width: 400px;
  height: fit-content;
  padding: 20px;
  text-align: start;
}

.site-prefs__section {
  &:first-of-type .site-prefs__heading {
    margin-top: 0;
  }
}

.site-prefs__heading {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  color: $black-100;
}

.site-prefs__divider {
  height: 0;
  margin: 18px 0;
  border-top: 1px solid $black-10;
}

.site-prefs__row {
  display: grid;
  gap: 10px;

  &--two {
    grid-template-columns: 1fr 1fr;
  }

  &--wrap {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

.site-prefs__grid-currency {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.site-prefs__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: $black-80;
  cursor: pointer;
  border: none;
  border-radius: 9999px;
  background: $black-05;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: $black-10;
  }

  &--compact {
    min-height: 40px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
  }

  &--active {
    font-weight: 700;
    color: $white;
    background: $gold-primary-100;

    &:hover {
      background: $gold-primary-80;
    }
  }
}
</style>
